/**
 * Subscription Widget Enhancer
 * Waits for Shopify Subscription App to render its fieldset,
 * then restructures it visually to match the custom design.
 * All native inputs/events are preserved untouched.
 */

(function () {
  const STYLES = `
    .shopify_subscriptions_fieldset {
      all: unset;
      display: block;
    }

    /* ── Wrapper card ── */
    .sow-card {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* ── Each option panel ── */
    .sow-panel {
      position: relative;
      border: 3px solid var(--color-orange);
      border-radius: 0;
      padding: 14px 16px 16px;
      background-color: rgba(var(--color-scheme-secondary-background), var(--tw-bg-opacity));
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      overflow: hidden;
    }

    .sow-panel:has(input[type="radio"]:checked),
    .sow-panel.sow-checked {
      border-color: var(--color-orange);
      background-color: rgba(var(--color-scheme-secondary-background), var(--tw-bg-opacity));
      box-shadow: 0 0 0 1px var(--color-orange);
    }

    /* ── Badge ── */
    .sow-badge {
      position: absolute;
      top: 0; left: 0; right: 0;
      background: var(--color-orange);
      color: var(--color-yellow);
      font-family: var(--button-font-stack);
      font-weight: var(--button-font-weight);
      font-size: 21px;
      text-align: center;
      padding: 5px 0;
      text-transform: uppercase;
    }

    .sow-panel--has-badge {
      padding-top: 38px;
    }

    /* ── Row: radio+title | prices ── */
    .sow-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      padding-top: 10px;
    }

    .sow-row-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    /* ── Hide native radio visually but keep it functional ── */
    .sow-panel input[type="radio"] {
      appearance: none;
      -webkit-appearance: none;
      width: 20px;
      height: 20px;
      min-width: 20px;
      border: 2px solid #9ab89a;
      border-radius: 50%;
      cursor: pointer;
      position: relative;
      transition: border-color 0.2s, background 0.2s;
      margin: 0;
    }

    .sow-panel input[type="radio"]:checked {
      border-color: var(--color-orange);
      background: var(--color-orange);
    }

    .sow-panel input[type="radio"]:checked::after {
      content: '';
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 8px; height: 8px;
      background: #fff;
      border-radius: 50%;
    }

    /* ── Title ── */
    .sow-title {
      font-family: var(--button-font-stack);
      font-weight: var(--button-font-weight);
      font-size: 22px;
      text-transform: var(--button-text-transform);
      letter-spacing: var(--button-letter-spacing);
      color: var(--color-orange);
    }

    /* ── Prices ── */
    .sow-prices {
      text-align: right;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-end;
      align-items: center;
      gap: 5px;
      flex-shrink: 0;
    }

    .sow-price {
      font-family: var(--main-font-stack);
      font-weight: 600;
      font-size: 20px;
      color: var(--color-orange);
      line-height: 1;
    }

    .sow-compare {
      font-size: 15px;
      color: var(--color-orange);
      text-decoration: line-through;
      line-height: 1;
    }

    .sow-per-day {
      display: none;
      width: 100%;
      font-size: 15px;
      color: rgba(var(--color-scheme-text), var(--tw-text-opacity));
      line-height: 1;
    }

    /* ── Sub-label (cadence / packs) ── */
    .sow-sub-label {
      display: none;
      font-size: 16px;
      color: rgba(var(--color-scheme-text), var(--tw-text-opacity));
      margin-top: 4px;
      margin-left: 30px;
    }

    /* ── Perks list (subscription only) ── */
    .sow-perks {
      list-style: none;
      margin: 10px 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .sow-perks li {
      font-size: 16px;
      padding-left: 22px;
      position: relative;
    }

    .sow-perks li::before {
      content: '✓';
      position: absolute;
      left: 0;
      font-weight: 700;
    }
    .sow-panel:not(.sow-panel--has-badge) li::before {
      content: 'X';
    }

    /* ── Shopify's own policy text ── */
    .shopify_subscriptions_app_policy {
      display: none;
      font-size: 12px;
      color: #666;
      margin-top: 8px;
    }

    /* ── Hide original card chrome ── */
    .plan_card {
      border: none !important;
      background: transparent !important;
      border-radius: 0 !important;
      padding: 0 !important;
    }

    .subscription_group {
      display: none !important;
    }
  `;

  /* ── Config — edit these to match your store ── */
  const CONFIG = {
    badgePrefix: (discount) => {
      if (discount === 30) return 'BEST FOR 2 PEOPLE: GET';
      return 'MOST POPULAR: GET';
    },

    subscriptionPerkTemplate: (discount) => [
      `${discount}% Off Every Order`,
      'Discounted Shipping',
      'Pause Or Cancel Any Time',
    ],

    oneTimePerkTemplate: (discount) => [
      `${discount}% More`,
      'No Free Shipping',
    ],

    perDay: (priceStr) => {
      const num = parseFloat(priceStr.replace(/[^0-9.]/g, ''));

      if (isNaN(num)) return null;

      return `$${(num / 28).toFixed(2)}/day`;
    },
  };

  function injectStyles() {
    if (document.getElementById('sow-styles')) return;

    const s = document.createElement('style');
    s.id = 'sow-styles';
    s.textContent = STYLES;

    document.head.appendChild(s);
  }

  function formatPrice(value) {
    return value.replace(/\s?USD/g, '').trim();
  }

  function getSubscriptionDiscount(subscriptionPrice, oneTimePrice) {
    const subNum = parseFloat(
      subscriptionPrice.replace(/[^0-9.]/g, '')
    );

    const oneTimeNum = parseFloat(
      oneTimePrice.replace(/[^0-9.]/g, '')
    );

    if (
      isNaN(subNum) ||
      isNaN(oneTimeNum) ||
      oneTimeNum <= subNum
    ) {
      return null;
    }

    return Math.round(
      ((oneTimeNum - subNum) / oneTimeNum) * 100
    );
  }

  function updateServingPrices(isSubscription) {
    document
      .querySelectorAll('.variant-savings--servings')
      .forEach((el) => {
        const originalPrice = parseInt(
          el.dataset.variantOriginalPrice,
          10
        );

        const servings = parseInt(
          el.dataset.variantServing,
          10
        );

        const discount = parseInt(
          el.dataset.subscriptionDiscount || 0,
          10
        );

        if (!originalPrice || !servings) return;

        const adjustedPrice = isSubscription
          ? originalPrice * (1 - discount / 100)
          : originalPrice;

        const perServing = adjustedPrice / 100 / servings;

        el.textContent = `$${perServing.toFixed(2)}/ serving`;
      });
  }

function updateProductPrice(priceStr) {
  const el = document.querySelector('[data-product-price]');
  if (!el) return;
  el.textContent = priceStr;
}

  function buildPanel({
    radio,
    title,
    price,
    comparePrice,
    subLabel,
    badge,
    perks,
  }) {
    const panel = document.createElement('div');

    panel.className =
      'sow-panel' + (badge ? ' sow-panel--has-badge' : '');

    if (radio.checked) {
      panel.classList.add('sow-checked');
    }

    if (badge) {
      const b = document.createElement('div');
      b.className = 'sow-badge';
      b.textContent = badge;
      panel.appendChild(b);
    }

    const row = document.createElement('div');
    row.className = 'sow-row';

    const left = document.createElement('div');
    left.className = 'sow-row-left';

    left.appendChild(radio);

    const titleSpan = document.createElement('span');
    titleSpan.className = 'sow-title';
    titleSpan.textContent = title;

    left.appendChild(titleSpan);
    row.appendChild(left);

    const prices = document.createElement('div');
    prices.className = 'sow-prices';

    const priceEl = document.createElement('span');
    priceEl.className = 'sow-price';
    priceEl.textContent = price;

    prices.appendChild(priceEl);

    if (comparePrice) {
      const cmp = document.createElement('span');
      cmp.className = 'sow-compare';
      cmp.textContent = comparePrice;
      prices.appendChild(cmp);
    }

    if (CONFIG.perDay && price) {
      const pd = CONFIG.perDay(price);

      if (pd) {
        const pdEl = document.createElement('span');
        pdEl.className = 'sow-per-day';
        pdEl.textContent = pd;
        prices.appendChild(pdEl);
      }
    }

    row.appendChild(prices);
    panel.appendChild(row);

    if (subLabel) {
      const sl = document.createElement('div');
      sl.className = 'sow-sub-label';
      sl.textContent = subLabel;
      panel.appendChild(sl);
    }

    if (perks && perks.length) {
      const ul = document.createElement('ul');
      ul.className = 'sow-perks';

      if (
        radio.dataset.radioType === 'one_time_purchase' &&
        !radio.checked
      ) {
        ul.style.display = 'none';
      }

      perks.forEach((p) => {
        const li = document.createElement('li');
        li.textContent = p;
        ul.appendChild(li);
      });

      panel.appendChild(ul);

      radio.addEventListener('change', () => {
        document.querySelectorAll('.sow-perks').forEach((list) => {
          const parentRadio = list
            .closest('.sow-panel')
            ?.querySelector('input[type="radio"]');

          if (
            parentRadio?.dataset.radioType ===
            'one_time_purchase'
          ) {
            list.style.display = parentRadio.checked
              ? 'flex'
              : 'none';
          }
        });
      });
    }

    radio.addEventListener('change', () => {
      document
        .querySelectorAll('.sow-panel')
        .forEach((p) => p.classList.remove('sow-checked'));

      if (radio.checked) {
        panel.classList.add('sow-checked');
      }

      updateServingPrices(
        radio.dataset.radioType === 'selling_plan'
      );
      updateProductPrice(
        formatPrice(radio.dataset.variantPrice || '')
      );
    });

    panel.addEventListener('click', (e) => {
      if (e.target !== radio) {
        radio.click();
      }
    });

    return panel;
  }

  function enhance(fieldset) {
    if (fieldset.dataset.sowEnhanced) return;

    fieldset.dataset.sowEnhanced = '1';

    injectStyles();

    const radios = Array.from(
      fieldset.querySelectorAll('input[type="radio"]')
    );

    if (!radios.length) return;

    const sortedRadios = radios.sort((a, b) => {
      const aIsSub = a.dataset.radioType === 'selling_plan';
      const bIsSub = b.dataset.radioType === 'selling_plan';

      if (aIsSub && !bIsSub) return -1;
      if (!aIsSub && bIsSub) return 1;

      return 0;
    });

    const subscriptionRadio = sortedRadios.find(
      (radio) => radio.dataset.radioType === 'selling_plan'
    );

    const oneTimeRadio = sortedRadios.find(
      (r) => r.dataset.radioType === 'one_time_purchase'
    );

    const oneTimePrice = formatPrice(
      oneTimeRadio?.dataset.variantPrice || ''
    );

    const subscriptionDiscount =
      getSubscriptionDiscount(
        formatPrice(
          subscriptionRadio?.dataset.variantPrice || ''
        ),
        oneTimePrice
      ) || 0;

    window.sowSubscriptionDiscount = subscriptionDiscount;

    if (subscriptionRadio && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;

      subscriptionRadio.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }

    const card = document.createElement('div');
    card.className = 'sow-card';


    sortedRadios.forEach((radio) => {
      const isOnetime =
        radio.dataset.radioType === 'one_time_purchase';

      const isSubscription =
        radio.dataset.radioType === 'selling_plan';

      const price = formatPrice(
        radio.dataset.variantPrice || ''
      );

      const comparePrice = formatPrice(
        radio.dataset.variantCompareAtPrice || ''
      );

      const discount = isSubscription
        ? getSubscriptionDiscount(price, oneTimePrice)
        : getSubscriptionDiscount(
            formatPrice(
              subscriptionRadio?.dataset.variantPrice || ''
            ),
            price
          );

      const badgeText =
        isSubscription && discount
          ? `${CONFIG.badgePrefix(discount)} ${discount}% OFF`
          : null;

      const perks = isSubscription
        ? CONFIG.subscriptionPerkTemplate(discount)
        : CONFIG.oneTimePerkTemplate(discount);

      let subLabel = '';

      if (isOnetime) {
        subLabel = '28 packs delivered once';
      } else {
        const li = radio.closest('li');

        if (li) {
          const span = li.querySelector(
            '.title_and_price_wrapper > span'
          );

          if (span) {
            subLabel = span.textContent.trim();
          }
        }
      }

      const panel = buildPanel({
        radio,
        title: isOnetime
          ? 'One Time Purchase'
          : 'Bi-Monthly Delivery',
        price,
        comparePrice: isSubscription ? comparePrice : '',
        subLabel,
        badge: badgeText,
        perks,
      });

      card.appendChild(panel);
    });

    const planCard = fieldset.querySelector('.plan_card');

    if (planCard) {
      planCard.insertAdjacentElement('beforebegin', card);
    } else {
      fieldset.prepend(card);
    }

    updateServingPrices(true);

    if (subscriptionRadio) {
      updateProductPrice(
        formatPrice(subscriptionRadio.dataset.variantPrice || '')
      );
    }
  }

  function init() {
  document
    .querySelectorAll('.shopify_subscriptions_fieldset')
    .forEach(enhance);

  const observer = new MutationObserver(() => {
    document
      .querySelectorAll(
        '.shopify_subscriptions_fieldset:not([data-sow-enhanced])'
      )
      .forEach(enhance);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('theme:variant:change', (e) => {
    const variantId = e.detail?.variant?.id;

    // Find the now-visible section for this variant
    const activeSection = variantId
      ? document.querySelector(
          `.shopify_subscriptions_app_block[data-variant-id="${variantId}"]`
        )
      : document.querySelector(
          '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)'
        );

    if (!activeSection) return;

    const fieldset = activeSection.querySelector(
      '.shopify_subscriptions_fieldset'
    );

    if (!fieldset) return;

    // Force subscription radio to be selected
    const subscriptionRadio = fieldset.querySelector(
      'input[type="radio"][data-radio-type="selling_plan"]'
    );

    if (subscriptionRadio) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }

    // Sync all sow-panel checked states
    fieldset.querySelectorAll('.sow-panel').forEach((panel) => {
      const radio = panel.querySelector('input[type="radio"]');
      panel.classList.toggle('sow-checked', !!radio?.checked);
    });

    updateServingPrices(true);
  });
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// Preselect 16 packets
document.addEventListener('DOMContentLoaded', () => {
  let hasPreselected = false;

  const preselect = () => {
    if (hasPreselected) return;

    const sixteenPackets = document.querySelector(
      'input[data-handle="personal-box-16-packets"]'
    );

    if (!sixteenPackets) return;

    hasPreselected = true;
    sixteenPackets.click();
  };

  // Wait for Alpine + theme to be ready
  document.addEventListener('theme:variant:change', () => {
    preselect();
  }, { once: true });

  // Fallback in case theme:variant:change already fired
  setTimeout(preselect, 800);
});